import React, { useState, useEffect } from 'react';
import { 
  Image, Calendar, Plus, Edit3, X, Loader2, ChevronRight,
  Instagram, Linkedin, Twitter, Youtube, Play, FileText,
  CheckCircle2, Clock, AlertTriangle, Eye, Send, Trash2,
  BarChart3, Filter, Search
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const PLATFORMS = {
  instagram: { icon: Instagram, color: 'bg-pink-500/20 text-pink-400', label: 'Instagram' },
  linkedin: { icon: Linkedin, color: 'bg-blue-500/20 text-blue-400', label: 'LinkedIn' },
  twitter: { icon: Twitter, color: 'bg-cyan-500/20 text-cyan-400', label: 'Twitter' },
  youtube: { icon: Youtube, color: 'bg-red-500/20 text-red-400', label: 'YouTube' },
  tiktok: { icon: Play, color: 'bg-purple-500/20 text-purple-400', label: 'TikTok' }
};

const SERIES = {
  always_elevating: { label: 'Always Elevating', color: 'bg-[#C4A052]/20 text-[#C4A052]' },
  film_room: { label: 'Film Room', color: 'bg-green-500/20 text-green-400' },
  beyond_the_game: { label: 'Beyond the Game', color: 'bg-blue-500/20 text-blue-400' },
  community_first: { label: 'Community First', color: 'bg-purple-500/20 text-purple-400' }
};

const PILLARS = [
  { id: 'performance', label: 'Performance', percent: 40 },
  { id: 'lifestyle', label: 'Lifestyle', percent: 25 },
  { id: 'purpose', label: 'Purpose', percent: 20 },
  { id: 'personal', label: 'Personal', percent: 15 }
];

const STATUSES = {
  draft: { label: 'Draft', color: 'bg-gray-500/20 text-gray-400', next: 'internal_review' },
  internal_review: { label: 'Internal Review', color: 'bg-yellow-500/20 text-yellow-400', next: 'ready_for_client' },
  ready_for_client: { label: 'Ready for Client', color: 'bg-[#C4A052]/20 text-[#C4A052]', next: null },
  changes_requested: { label: 'Changes Requested', color: 'bg-orange-500/20 text-orange-400', next: 'internal_review' },
  approved: { label: 'Approved', color: 'bg-green-500/20 text-green-400', next: 'scheduled' },
  scheduled: { label: 'Scheduled', color: 'bg-blue-500/20 text-blue-400', next: 'published' },
  published: { label: 'Published', color: 'bg-emerald-500/20 text-emerald-400', next: null },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400', next: null }
};

const AEAdminContentStudio = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Create form state
  const [newContent, setNewContent] = useState({
    title: '',
    platform: 'instagram',
    content_type: 'post',
    series: '',
    pillar: '',
    monthly_theme: '',
    caption: '',
    scheduled_date: ''
  });

  useEffect(() => {
    if (userEmail) {
      fetchContent();
    }
  }, [userEmail]);

  const fetchContent = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/content?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setContent(data.data);
      }
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContent = async () => {
    if (!newContent.title.trim()) return;
    
    setActionLoading('create');
    try {
      const res = await fetch(`${API}/api/always-elevate/content?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newContent,
          client_visible: false
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowCreateModal(false);
        setNewContent({
          title: '',
          platform: 'instagram',
          content_type: 'post',
          series: '',
          pillar: '',
          monthly_theme: '',
          caption: '',
          scheduled_date: ''
        });
        fetchContent();
      }
    } catch (err) {
      console.error('Error creating content:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendToClient = async (contentId) => {
    setActionLoading('send');
    try {
      const res = await fetch(`${API}/api/always-elevate/content/${contentId}/approve?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'approve',
          comment: 'Sent for client review'
        })
      });
      const data = await res.json();
      if (data.success) {
        fetchContent();
        setSelectedContent(prev => prev ? { ...prev, status: 'ready_for_client' } : null);
      }
    } catch (err) {
      console.error('Error sending to client:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredContent = content.filter(item => {
    if (filter !== 'all' && item.status !== filter) return false;
    if (searchQuery && !item.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Content mix analysis
  const contentMix = PILLARS.map(pillar => {
    const count = content.filter(c => c.pillar === pillar.id && c.status !== 'draft').length;
    const total = content.filter(c => c.status !== 'draft').length || 1;
    const actual = Math.round((count / total) * 100);
    return { ...pillar, actual, count };
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ELLE MAXWELL AGENCY</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }}>
                Content Studio
              </h1>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Content
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Content Mix Analysis */}
        <div className="bg-black border border-white/20 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#C4A052]" />
              Content Mix (Target: 40/25/20/15)
            </h3>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {contentMix.map(pillar => (
              <div key={pillar.id} className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{pillar.label}</span>
                  <span className={`text-xs ${
                    Math.abs(pillar.actual - pillar.percent) <= 5 ? 'text-green-400' : 'text-yellow-400'
                  }`}>
                    {pillar.actual}% / {pillar.percent}%
                  </span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      Math.abs(pillar.actual - pillar.percent) <= 5 ? 'bg-green-500' : 'bg-yellow-500'
                    }`}
                    style={{ width: `${Math.min(pillar.actual, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{pillar.count} items</p>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            {[
              { id: 'all', label: 'All' },
              { id: 'draft', label: 'Drafts' },
              { id: 'internal_review', label: 'Internal Review' },
              { id: 'ready_for_client', label: 'With Client' },
              { id: 'approved', label: 'Approved' },
              { id: 'scheduled', label: 'Scheduled' },
              { id: 'published', label: 'Published' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filter === f.id 
                    ? 'bg-[#C4A052] text-black font-medium' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-black border border-white/20 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-[#C4A052] w-64"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Content List */}
          <div className="lg:col-span-1 space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
            {filteredContent.length === 0 ? (
              <div className="bg-black border border-white/20 rounded-xl p-8 text-center">
                <Image className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                <p className="text-gray-400">No content found</p>
              </div>
            ) : (
              filteredContent.map(item => {
                const platform = PLATFORMS[item.platform] || PLATFORMS.instagram;
                const PlatformIcon = platform.icon;
                const status = STATUSES[item.status] || STATUSES.draft;
                
                return (
                  <div
                    key={item.id}
                    onClick={() => setSelectedContent(item)}
                    className={`bg-white/5 border rounded-xl p-4 cursor-pointer ${
                      selectedContent?.id === item.id ? 'border-[#C4A052]' : 'border-white/10'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${platform.color}`}>
                        <PlatformIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm truncate">{item.title}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded ${status.color}`}>
                            {status.label}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Content Detail */}
          <div className="lg:col-span-2">
            {selectedContent ? (
              <div className="bg-black border border-white/20 rounded-xl overflow-hidden">
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${PLATFORMS[selectedContent.platform]?.color}`}>
                          {PLATFORMS[selectedContent.platform]?.label}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${STATUSES[selectedContent.status]?.color}`}>
                          {STATUSES[selectedContent.status]?.label}
                        </span>
                      </div>
                      <h2 className="text-xl font-bold">{selectedContent.title}</h2>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Caption */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Caption</h3>
                    <div className="bg-white/5 rounded-lg p-4 text-sm text-gray-300 whitespace-pre-wrap">
                      {selectedContent.caption || 'No caption provided.'}
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="grid grid-cols-2 gap-4">
                    {selectedContent.series && (
                      <div>
                        <h4 className="text-xs text-gray-500 mb-1">Series</h4>
                        <span className={`text-xs px-2 py-1 rounded ${SERIES[selectedContent.series]?.color}`}>
                          {SERIES[selectedContent.series]?.label}
                        </span>
                      </div>
                    )}
                    {selectedContent.pillar && (
                      <div>
                        <h4 className="text-xs text-gray-500 mb-1">Pillar</h4>
                        <span className="text-sm capitalize">{selectedContent.pillar}</span>
                      </div>
                    )}
                    {selectedContent.scheduled_date && (
                      <div>
                        <h4 className="text-xs text-gray-500 mb-1">Scheduled</h4>
                        <span className="text-sm">{new Date(selectedContent.scheduled_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Admin Actions */}
                  <div className="border-t border-white/10 pt-6">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Actions</h3>
                    <div className="flex flex-wrap gap-3">
                      {selectedContent.status === 'draft' && (
                        <button className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors">
                          <Eye className="w-4 h-4" />
                          Submit for Review
                        </button>
                      )}
                      {selectedContent.status === 'internal_review' && (
                        <button
                          onClick={() => handleSendToClient(selectedContent.id)}
                          disabled={actionLoading === 'send'}
                          className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg hover:bg-[#D4B062] transition-colors disabled:opacity-50"
                        >
                          {actionLoading === 'send' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                          Send to Akayleb
                        </button>
                      )}
                      {selectedContent.status === 'approved' && (
                        <button className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors">
                          <Calendar className="w-4 h-4" />
                          Schedule
                        </button>
                      )}
                      <button className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors">
                        <Edit3 className="w-4 h-4" />
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
                <Image className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">Select Content</h3>
                <p className="text-gray-500 text-sm">Click on a content item to view and manage</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">New Content</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Title</label>
                <input
                  type="text"
                  value={newContent.title}
                  onChange={(e) => setNewContent(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  placeholder="Content title..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Platform</label>
                  <select
                    value={newContent.platform}
                    onChange={(e) => setNewContent(prev => ({ ...prev, platform: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  >
                    {Object.entries(PLATFORMS).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Series</label>
                  <select
                    value={newContent.series}
                    onChange={(e) => setNewContent(prev => ({ ...prev, series: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  >
                    <option value="">Select series...</option>
                    {Object.entries(SERIES).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Pillar</label>
                  <select
                    value={newContent.pillar}
                    onChange={(e) => setNewContent(prev => ({ ...prev, pillar: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  >
                    <option value="">Select pillar...</option>
                    {PILLARS.map(p => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Scheduled Date</label>
                  <input
                    type="date"
                    value={newContent.scheduled_date}
                    onChange={(e) => setNewContent(prev => ({ ...prev, scheduled_date: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Caption</label>
                <textarea
                  value={newContent.caption}
                  onChange={(e) => setNewContent(prev => ({ ...prev, caption: e.target.value }))}
                  rows={4}
                  className="w-full bg-black border border-white/20 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:border-[#C4A052]"
                  placeholder="Write the caption..."
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateContent}
                disabled={!newContent.title.trim() || actionLoading === 'create'}
                className="px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors disabled:opacity-50"
              >
                {actionLoading === 'create' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Create Content'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AEAdminContentStudio;
